// @ts-nocheck - formatter / Table
import React, { useState } from 'react';
import i18next from 'i18next';
import { gameKeys, exportGameKeysData } from '../../../actions/metricActions';
import { NavLink, useLocation } from 'react-router-dom';
import { Table, SimpleTextFilter, getVisibleColumns } from '../../common/Table';
import { CommaNumber_NoDecimal } from '../../../util/formatter';
import Loading from '../../common/Loading';
import KwPanel from '../../common/KwPanel';
import {
  useTableSettings,
  getTableSettingsDefaults,
} from '../../../hooks/useTableSettings';
import KeyProviderLink from '../../common/links/KeyProviderLink';
import PlatformLink from '../../common/links/PlatformLink';
import skuHelp from '../../../constants/help/skuHelp';
import { scopes as tableScopes } from '../../../constants/scopes';
import { useTranslation } from 'react-i18next';

export interface GameKeysListProps {
  reload?: any;
  setBreadcrumb: (crumb: any) => void;
  tableViewSwitcher?: React.ReactNode;
}

function GameKeysList({ reload, setBreadcrumb, tableViewSwitcher }: GameKeysListProps) {
  const location = useLocation();
  const { t } = useTranslation();
  const language = i18next.language;
  const allColumns = [
    'gameName',
    'platformName',
    'keyProviderName',
    'isAvailableCodeCount',
    'isAllocatedCodeCount',
    'isSoldCodeCount',
    'isActivatedCodeCount',
    'isDisabledCodeCount',
    'isExpiredCodeCount',
  ];
  const defaultVisibleColumns = allColumns;

  const [gamekeys, setGameKeys] = useState<any>();
  const params = new URLSearchParams(location.search);
  const { tableSettings, handleSetTableSettings: setTableSettings, filters, filterChange, isLoading } =
    useTableSettings(
      'gameskeyslist-settings',
      getTableSettingsDefaults({
        defaultSort: 'gameName',
        defaultVisibleColumns,
      }),
      null,
      gameKeys,
      setGameKeys,
      reload,
      t('Failed to load game keys')
    );

  let columnsConfig = [
    {
      Key: 'gameName',
      Label: t('Game'),
      Render: (item: any) => (
        <>
          <NavLink to={`/sku/${item.skuId}`} className="game-title">
            {item.gameName}
          </NavLink>
          <br />
          <NavLink to={`/sku/${item.skuId}`}>{item.skuName}</NavLink>
        </>
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'platformName',
      Label: t('Platform'),
      Render: (item: any) => (
        <PlatformLink id={item.platformId} name={item.platformName} />
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'keyProviderName',
      Label: t('Key provider'),
      Render: (item: any) => (
        <KeyProviderLink id={item.keyProviderId} name={item.keyProviderName} />
      ),
      Filter: {
        Render: (col: any) => (
          <SimpleTextFilter
            Key={col.Key}
            Value={col.filterValue ?? params.get(`f_${col.Key}`) ?? ''}
            onChange={(value) => filterChange(col.Key, value)}
          />
        ),
      },
    },
    {
      Key: 'isAvailableCodeCount',
      Label: t('Pooled'),
      helpContent: skuHelp.SKU_POOL_TABLE_POOLED[language],
      className: 'text-right num no-wrap',
      Render: (row: any) => CommaNumber_NoDecimal(row.isAvailableCodeCount),
    },
    {
      Key: 'isAllocatedCodeCount',
      Label: t('Delivered'),
      className: 'text-right num no-wrap',
      Render: (row: any) => CommaNumber_NoDecimal(row.isAllocatedCodeCount),
    },
    {
      Key: 'isSoldCodeCount',
      Label: t('Reported sold'),
      className: 'text-right num no-wrap',
      Render: (item: any) =>
        item.isSoldCodeCount > 0 ? (
          <NavLink to={`/keys/reported-sold/?${tableScopes.SKU}=${item.skuId}`}>
            {CommaNumber_NoDecimal(item.isSoldCodeCount)}
          </NavLink>
        ) : (
          0
        ),
    },
    {
      Key: 'isActivatedCodeCount',
      Label: t('Activated'),
      className: 'text-right num no-wrap',
      Render: (item: any) =>
        item.isActivatedCodeCount > 0 ? (
          <NavLink
            to={`/keys/reported-activated/?${tableScopes.SKU}=${item.skuId}`}
          >
            {CommaNumber_NoDecimal(item.isActivatedCodeCount)}
          </NavLink>
        ) : (
          0
        ),
    },
    {
      Key: 'isDisabledCodeCount',
      Label: t('Disabled'),
      className: 'text-right num no-wrap',
      Render: (item: any) =>
        item.isDisabledCodeCount > 0 ? (
          <NavLink
            to={`/keys/reported-disabled/?${tableScopes.SKU}=${item.skuId}`}
          >
            {CommaNumber_NoDecimal(item.isDisabledCodeCount)}
          </NavLink>
        ) : (
          0
        ),
    },
    {
      Key: 'isExpiredCodeCount',
      Label: t('Expired'),
      className: 'text-right num no-wrap',
      Render: (row: any) => CommaNumber_NoDecimal(row.isExpiredCodeCount),
    },
  ];

  const visibleColumns = tableSettings.columns ?? defaultVisibleColumns;
  const columns = getVisibleColumns(visibleColumns, columnsConfig);

  return gamekeys ? (
    <KwPanel className="mb-4">
      <Table
        columns={columns}
        allColumns={allColumns}
        rows={gamekeys.data.map((x: any) => ({ Key: x.skuId, ...x }))}
        totalRowCount={gamekeys.totalRowCount}
        filteredRowCount={gamekeys.filteredRowCount}
        scopes={gamekeys.scopes}
        tableSettings={tableSettings}
        setTableSettings={setTableSettings}
        isLoading={isLoading}
        filters={filters}
        filterChange={filterChange}
        exportData={exportGameKeysData}
        setBreadcrumb={setBreadcrumb}
        tableActionBarButtons={tableViewSwitcher}
      />
    </KwPanel>
  ) : (
    <Loading />
  );
}

export default GameKeysList;
